/**
 * Wonach eine Auswahlliste sucht.
 *
 * Die Einträge der DIN 277 heißen „NUF 4.1 - Lagerräume": erst die Normnummer, dann der Name. Wer
 * einen Raum einordnet, denkt aber am Namen entlang und tippt „lag" — nicht „NUF 4". Der
 * Standardfilter von cmdk bewertet nach Ähnlichkeit und ist dabei nicht vorhersagbar; hier steht
 * stattdessen eine Regel, die sich aussprechen lässt:
 *
 * **Gesucht wird als Teilzeichenkette, im Namen wie in der Nummer — der Name zuerst.**
 *
 * Beides zu durchsuchen ist nötig, weil auch nach der Nummer gesucht wird: Wer „8.3" eingibt, meint
 * die Heizung. Und der Name wiegt schwerer, damit „bl" nicht zuerst eine Nummer findet, in der
 * zufällig ein „bl" steckt.
 */

/** Kleingeschrieben und ohne Akzente — „Kuchen" soll „Küchen" finden. */
const fold = (value: string): string =>
    value
        .toLowerCase()
        .normalize('NFD')
        // Kombinierende Zeichen entfernen: aus „ü" wird „u", aus „é" wird „e".
        .replace(/[̀-ͯ]/g, '')
        .replace(/ß/g, 'ss');

/**
 * Der Teil hinter dem ersten Gedankenstrich — der Name ohne seine Normnummer.
 *
 * Der **erste** Strich trennt, nicht der letzte: „NUF 4.4 - Annahme- und Ausgaberäume" trägt weitere
 * Striche im Namen, und ab dem letzten bliebe davon nur „ und Ausgaberäume".
 *
 * Wo kein Strich steht, ist der ganze Text der Name; Einträge ohne Normnummer verlieren dadurch
 * nichts.
 */
export const nameAfterNumber = (label: string): string => {
    const at = label.indexOf(' - ');

    return at === -1 ? label : label.slice(at + 3);
};

/**
 * Wie gut ein Eintrag zur Sucheingabe passt: 2 für einen Treffer im Namen, 1 anderswo, 0 gar nicht.
 *
 * Die Zahlen sind Rangfolge, kein Maß — cmdk sortiert danach und blendet die Null aus.
 */
export const optionSearchScore = (label: unknown, search: unknown): number => {
    const text = typeof label === 'string' ? label : '';
    const query = typeof search === 'string' ? search.trim() : '';

    // Ohne Eingabe steht die Liste, wie sie sortiert ist.
    if (!query) return 1;

    const foldedQuery = fold(query);

    if (fold(nameAfterNumber(text)).includes(foldedQuery)) return 2;

    return fold(text).includes(foldedQuery) ? 1 : 0;
};
